import { Injectable, Signal, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_PATHS } from '../config/api-paths';
import { ScaApi } from '../tokens/sca.token';
import {
  PendingScaChallenge,
  ScaChallengeRequest,
  ScaResult,
  ScaSendRequest,
  ScaSendResponse,
  ScaVerifyRequest,
  ScaVerifyResponse,
} from '../models/sca.model';

// `PendingScaChallenge` moved to `../models/sca.model` (EM-1445) so the DI token can name it in
// its contract without a circular import. Re-exported here so existing importers keep working.
export type { PendingScaChallenge } from '../models/sca.model';

/**
 * REST-only implementation of `ScaApi` (EM-308).
 *
 * Calls:
 *   POST /services/embedded/api/me/sca/send
 *   POST /services/embedded/api/me/sca/verify
 *
 * `challenge()` is the high-level entry the pillars use: it publishes a `PendingScaChallenge`
 * on the `pending` signal and returns a Promise. The shared `<lendox-sca-modal>` (mounted once at
 * the APPLICATION root — `app.ts`) reacts to `pending`, runs the send/verify exchange, and resolves
 * the Promise with the minted `{ scaToken, expiresAt }` — which the caller attaches as `X-SCA-Token`.
 *
 * The returned Promise settles ONLY when that modal is mounted. It used to live at the SME shell
 * root, so a `challenge(...)` raised from any other host (lender / E-D representatives, the account
 * page's card request) awaited forever — no dialog, no request, no timeout (EM-1445). Keep the
 * mount at the root: this service is app-wide, so its counterpart must be too.
 *
 * Rides the Keycloak bearer via `authInterceptor`. Rule 07 / Rule 10 — no Mock; Playwright
 * intercepts via `page.route()`.
 */
@Injectable()
export class ScaRestService implements ScaApi {
  private readonly http = inject(HttpClient);
  private readonly base = API_PATHS.ME_SCA;

  private readonly pendingSig = signal<PendingScaChallenge | null>(null);

  /** The shared modal observes this to know when (and for what) to open. */
  readonly pending: Signal<PendingScaChallenge | null> = this.pendingSig.asReadonly();

  send(request: ScaSendRequest): Observable<ScaSendResponse> {
    return this.http.post<ScaSendResponse>(`${this.base}/send`, request);
  }

  verify(request: ScaVerifyRequest): Observable<ScaVerifyResponse> {
    return this.http.post<ScaVerifyResponse>(`${this.base}/verify`, request);
  }

  challenge(request: ScaChallengeRequest): Promise<ScaResult> {
    return new Promise<ScaResult>((resolve, reject) => {
      this.pendingSig.set({
        request,
        resolve: (result) => {
          this.pendingSig.set(null);
          resolve(result);
        },
        reject: (reason) => {
          this.pendingSig.set(null);
          reject(reason);
        },
      });
    });
  }
}
